import * as React from "react"
import { Settings2, Sun, Moon, Plus, LogOut, Menu } from "lucide-react"
import { useNavigate, useLocation } from "react-router-dom"
import { useOktaAuth } from "@okta/okta-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { useTheme } from "@/components/shared/theme-provider"
import { AlfredBulkImportProgress } from "@/components/alfred/bulk-import/alfred-bulk-import-progress"
import { useAlfredBulkImportJobContext } from "@/components/alfred/bulk-import/alfred-bulk-import-job-context"
import { useSmithersBulkImportJobContext } from "@/components/smithers/bulk-import/smithers-bulk-import-job-context"
import { resetAlfredTour } from "@/lib/alfred-tour"
import { isSmithersEnabled } from "@/lib/feature-flags"

interface DashboardHeaderProps {
  onAddDevice?: () => void
  onAddClient?: () => void
  onAddProVenueInstance?: () => void
  bulkAddPath?: string
  onBulkAdd?: () => void
}

function getInitials(name: string | undefined): string {
  if (!name) return "U"
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

export function DashboardHeader({
  onAddDevice,
  onAddClient,
  onAddProVenueInstance,
  bulkAddPath,
  onBulkAdd,
}: DashboardHeaderProps) {
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { oktaAuth, authState } = useOktaAuth()
  const { dismissJob, error: bulkImportError, isLoading, job, jobId } =
    useAlfredBulkImportJobContext()
  const smithersImport = useSmithersBulkImportJobContext()
  const smithersEnabled = isSmithersEnabled()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

  const handleLogout = async () => {
    resetAlfredTour()
    await oktaAuth.signOut({
      postLogoutRedirectUri: `${window.location.origin}/login`,
    })
  }

  const userName = authState?.idToken?.claims.name as string | undefined
  const userEmail = authState?.idToken?.claims.email as string | undefined
  const initials = getInitials(userName)

  const sections = [
    { name: "Alfred", path: "/alfred", defaultPath: "/alfred/clients" },
    ...(smithersEnabled ? [{ name: "Smithers", path: "/smithers", defaultPath: "/smithers/clients" }] : []),
    { name: "Go-Ahead Pay", path: "/go-ahead-pay", defaultPath: "/go-ahead-pay/clients" },
  ]

  const goAheadPaySubTabs = [
    { name: "Clients", path: "/go-ahead-pay/clients" },
    { name: "Devices", path: "/go-ahead-pay/devices" },
  ]

  const alfredSubTabs = [
    { name: "Clients", path: "/alfred/clients" },
    { name: "Devices", path: "/alfred/devices" },
    { name: "Global", path: "/alfred/global" },
  ]

  const smithersSubTabs = [
    { name: "Clients", path: "/smithers/clients" },
    { name: "Devices", path: "/smithers/devices" },
    { name: "Global", path: "/smithers/global" },
    { name: "ProVenue Instance Manager", path: "/smithers/provenue-instances" },
  ]

  const goAheadPayPaths = new Set([
    "/clients",
    "/devices",
    "/go-ahead-pay",
    "/go-ahead-pay/clients",
    "/go-ahead-pay/devices",
  ])
  const alfredPaths = new Set(["/", "/alfred", "/alfred/global", "/alfred/clients", "/alfred/devices"])

  const isGoAheadPaySection = goAheadPayPaths.has(location.pathname)
  const isSmithersSection =
    smithersEnabled && (location.pathname === "/smithers" || location.pathname.startsWith("/smithers/"))
  const isAlfredSection =
    alfredPaths.has(location.pathname) ||
    location.pathname.startsWith("/alfred/devices/") ||
    location.pathname.startsWith("/alfred/clients/")

  const isDevicesPage =
    location.pathname === "/go-ahead-pay/devices" ||
    location.pathname === "/alfred/devices" ||
    location.pathname === "/smithers/devices" ||
    location.pathname === "/devices" ||
    /^\/alfred\/clients\/\d+\/devices$/.test(location.pathname)
  const isClientsPage =
    location.pathname === "/go-ahead-pay/clients" ||
    location.pathname === "/alfred/clients" ||
    location.pathname === "/smithers/clients" ||
    location.pathname === "/clients" ||
    location.pathname === "/"

  const handleTabClick = (path: string) => {
    navigate(path)
    setIsMobileMenuOpen(false)
  }

  const isActiveSection = (sectionPath: string) => {
    if (sectionPath === "/go-ahead-pay") return isGoAheadPaySection
    if (sectionPath === "/alfred") return isAlfredSection
    if (sectionPath === "/smithers") return isSmithersSection
    return false
  }

  const subTabsForSection = (sectionPath: string) => {
    if (sectionPath === "/go-ahead-pay") return goAheadPaySubTabs
    if (sectionPath === "/alfred") return alfredSubTabs
    if (sectionPath === "/smithers") return smithersSubTabs
    return []
  }

  const currentSubTabs = isGoAheadPaySection
    ? goAheadPaySubTabs
    : isAlfredSection
      ? alfredSubTabs
      : isSmithersSection
        ? smithersSubTabs
        : []

  const isActiveSubTab = (path: string) =>
    location.pathname === path ||
    (path === "/alfred/devices" && location.pathname.startsWith("/alfred/devices/")) ||
    (path === "/alfred/clients" && location.pathname.startsWith("/alfred/clients/")) ||
    (path === "/smithers/clients" && location.pathname.startsWith("/smithers/clients/")) ||
    (path === "/smithers/devices" && location.pathname.startsWith("/smithers/devices/")) ||
    (path === "/go-ahead-pay/clients" && location.pathname === "/clients") ||
    (path === "/go-ahead-pay/devices" && location.pathname === "/devices") ||
    (path === "/alfred/clients" && (location.pathname === "/alfred" || location.pathname === "/"))

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-backdrop-filter:bg-card/80">
        <div className="container mx-auto px-4 h-16 grid grid-cols-[1fr_auto_1fr] items-center">
        {/* Logo + Mobile Nav */}
        <div className="flex items-center gap-2 justify-self-start">
          {/* Desktop logo */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="hidden md:flex items-center gap-2 rounded-md hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Go to home"
          >
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Settings2 className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-lg text-foreground">Device Config</span>
          </button>

          {/* Mobile hamburger + compact logo */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground shadow-sm hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0">
                <SheetHeader className="px-4 pt-4 pb-2 border-b border-border flex flex-row items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                    <Settings2 className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <SheetTitle className="text-base font-semibold">Device Config</SheetTitle>
                </SheetHeader>
                <nav className="py-2">
                  {sections.map((section) => {
                    const active = isActiveSection(section.path)
                    const sectionSubTabs = subTabsForSection(section.path)
                    return (
                      <div key={section.name}>
                        <button
                          type="button"
                          onClick={() => handleTabClick(section.defaultPath)}
                          className={`flex w-full items-center px-4 py-2 text-sm font-semibold text-left transition-colors ${
                            active
                              ? "bg-secondary text-foreground"
                              : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                          }`}
                        >
                          {section.name}
                        </button>
                        {active && sectionSubTabs.length > 0 && (
                          <div className="pl-2">
                            {sectionSubTabs.map((subTab) => (
                              <button
                                key={subTab.name}
                                type="button"
                                onClick={() => handleTabClick(subTab.path)}
                                className={`flex w-full items-center px-6 py-2 text-sm font-medium text-left transition-colors ${
                                  isActiveSubTab(subTab.path)
                                    ? "bg-secondary text-foreground"
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                                }`}
                              >
                                {subTab.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </nav>
              </SheetContent>
            </Sheet>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="font-semibold text-base text-foreground hover:opacity-80 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
              aria-label="Go to home"
            >
              Device Config
            </button>
          </div>
        </div>

        {/* Section Navigation - desktop only */}
        <nav className="hidden md:flex items-center gap-1 justify-self-center">
          {sections.map((section) => {
            const isActive = isActiveSection(section.path)
            return (
              <button
                key={section.name}
                onClick={() => navigate(section.defaultPath)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                }`}
              >
                {section.name}
              </button>
            )
          })}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-3 justify-self-end">
          {isDevicesPage && (isAlfredSection || isSmithersSection) ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-tour="add-device">
                  <Plus data-icon="inline-start" />
                  <span className="hidden xs:inline">Add Device</span>
                  <span className="xs:hidden">Add</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem onSelect={onAddDevice}>Add 1</DropdownMenuItem>
                  <DropdownMenuItem onSelect={onBulkAdd ?? (() => navigate(bulkAddPath ?? (isSmithersSection ? "/smithers/devices/bulk-add" : "/alfred/devices/bulk-add")))}>
                    Bulk add
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : isDevicesPage ? (
            <Button onClick={onAddDevice} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Add Device</span>
              <span className="xs:hidden">Add</span>
            </Button>
          ) : null}
          {location.pathname === "/smithers/provenue-instances" && onAddProVenueInstance && (
            <Button onClick={onAddProVenueInstance} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Add Instance</span>
              <span className="xs:hidden">Add</span>
            </Button>
          )}
          {isClientsPage && onAddClient && (
            <Button
              onClick={onAddClient}
              data-tour="add-client"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="hidden xs:inline">Add Client</span>
              <span className="xs:hidden">Add</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            <span className="sr-only">Toggle theme</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full">
                <Avatar className="h-8 w-8 cursor-pointer hover:opacity-80 transition-opacity">
                  <AvatarImage src="/diverse-user-avatars.png" alt="User" />
                  <AvatarFallback className="bg-secondary text-foreground text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  {userName && <p className="text-sm font-medium leading-none">{userName}</p>}
                  {userEmail && (
                    <p className="text-xs leading-none text-muted-foreground">{userEmail}</p>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        </div>

        {/* Secondary sub-nav for the active section - desktop */}
        {currentSubTabs.length > 0 && (
          <div className="hidden md:block border-t border-border bg-card/60">
            <div className="container mx-auto px-4 h-11 flex items-center gap-1">
              {currentSubTabs.map((subTab) => {
                const isActive = isActiveSubTab(subTab.path)
                return (
                  <button
                    key={subTab.name}
                    onClick={() => navigate(subTab.path)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? "bg-secondary text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    }`}
                  >
                    {subTab.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </header>
      {jobId ? (
        <AlfredBulkImportProgress
          jobId={jobId}
          job={job}
          error={bulkImportError}
          isLoading={isLoading}
          onDismiss={dismissJob}
        />
      ) : null}
      {smithersEnabled && smithersImport.jobId ? (
        <AlfredBulkImportProgress productName="Smithers" jobId={smithersImport.jobId} job={smithersImport.job} error={smithersImport.error} isLoading={smithersImport.isLoading} onDismiss={smithersImport.dismissJob} />
      ) : null}
    </>
  )
}
